import { useCallback } from "react";
import {
  onlineManager,
  QueryKey,
  useIsRestoring,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { collection, getDocs } from "firebase/firestore";
import { AxiosError, AxiosResponse } from "axios";

import { apiClient } from "@/api/api.ts";
import { getPhenotypes } from "@/features/Animals/animalApiSlice.ts";
import { db } from "@/config/firebase.ts";
import { FormValues } from "@/models/Sila.Animals.schema.ts";
import * as XLSX from "xlsx";
import { toast } from "sonner";

export const fetchAllAnimals = async () => {
  const response = await apiClient.get("/admin/allAnimals");

  return response.data;
};

export const fetchAllFarms = async () => {
  const response = await apiClient.get("/admin/allFarms");

  return response.data;
};

export const fetchAllSupervisors = async () => {
  const response = await apiClient.get("/admin/allSupervisors");

  return response.data;
};

export const fetchAllFarmers = async () => {
  const response = await apiClient.get("/admin/allFarmers");

  return response.data;
};

export const fetchAllPhenotype = async () => {
  const response = await getPhenotypes();

  return response.data;
};

export const registeringAdminAnimal = async (data: FormValues) => {
  try {
    await apiClient.post("/admin/add-new-Animal", data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetAllAnimals = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allAnimals"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/allAnimals", {
            params: { _fresh: Date.now() },
            headers: { "Cache-Control": "no-cache" },
          });

          return  response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allAnimals"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialData: () => {
      return queryClient.getQueryData(["allAnimals"]);
    },
    meta: {
      syncAction: async () => {
        const response = await apiClient.get("/admin/allAnimals");
        return response.data;
      },
    },
  });
};

export const useGetAllSupervisors = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allSupervisors"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/allSupervisors");

          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allSupervisors"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    staleTime: Infinity,
    refetchIntervalInBackground: true,
    initialData: () => {
      return queryClient.getQueryData(["allSupervisors"]);
    },
  });
};

export const useGetAllOfficials = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allOfficials"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/allOfficials");

          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allOfficials"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(["allOfficials"]);
    },
    meta: {
      syncAction: async () => {
        const response = await apiClient.get("/admin/allOfficials");

        return response.data;
      },
    },
  });
};

export const useGetAllFarms = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allFarms"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/allFarms");

          return response.data ;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allFarms"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(["allFarms"]);
    },
  });
};

export const useGetAllFarmers = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allFarmers"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/allFarmers");

          // console.log ( response.data )
          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allFarmers"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    // staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(["allFarmers"]);
    },
  });
};

export const useGetPhenotype = () => {
  // console.log("useGetPhenotype called");
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["phenotype"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/admin/phenotype", {
            withCredentials: false,
          });

          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["phenotype"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: 5 * 60 * 1000, // 5 minutes (adjust as needed)
    initialData: () => {
      return queryClient.getQueryData(["phenotype"]);
    },
  });
};

export const useGetFarmerAnimals = (id: string) => {
  const queryClient = useQueryClient();
  const isRestoring = useIsRestoring();

  return useQuery({
    queryKey: ["farmerOwnedAnimals", id],
    queryFn: async () => {
      if (!id) {
        throw new Error("User ID is not available"); // or
        // return
        // a
        // default
        // value,
        // or...
      }
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get(`/animals/farmer/${id}`, {
            params: { _fresh: Date.now() },
            headers: { "Cache-Control": "no-cache" },
          });

          return response.data;
        } catch (error) {
          // console.log(error);
          // throw error // Re-throw the error to be handled by
          // React Query
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedData = queryClient.getQueryData(["farmerOwnedAnimals", id]);

        if (cachedData) {
          return cachedData;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    staleTime: 0,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialData: () => {
      return queryClient.getQueryData(["farmerOwnedAnimals"]);
    },
  });
};

const fetchNotifications = async () => {
  // use notifications for dev and notificationsMain for prod
  const querySnapshot = await getDocs(collection(db, "notificationsMain"));

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

const fetchRequests = async () => {
  // use requests for dev and requestsMain for prod
  const querySnapshot = await getDocs(collection(db, "requestsMain"));

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const useNotifications = () => {
  return useQuery({
    queryKey: ["notifications"], // Unique key for caching
    queryFn: fetchNotifications, // Function to fetch data
    staleTime: 1000 * 60 * 5, // Data is considered fresh for 5
    // minutes
  });
};

export const useRequests = () => {
  return useQuery({
    queryKey: ["requests"], // Unique key for caching
    queryFn: fetchRequests, // Function to fetch data
    staleTime: 1000 * 60 * 5, // Data is considered fresh for 5
    // minutes
  });
};

export const handleExport = async (
  format: "csv" | "tsv" | "xlsx",
): Promise<void> => {
  try {
    const response = await apiClient.get(`/admin/export?format=${format}`, {
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("Download", `Animals-Sheet.${format}`); // File name
    document.body.append(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Export Failed:", error);
  }
};

export const useExportData = () => {
  return useMutation<void, Error, "csv" | "tsv" | "xlsx">({
    mutationFn: async (format) => {
      const response: AxiosResponse<Blob> = await apiClient.get(
        `/admin/export?format=${format}`,
        {
          responseType: "blob", // Ensure the response is treated as a binary file
        },
      );

      // Create a download
      // link for the file
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = blobUrl;
      link.setAttribute("download", `Animals-Sheet.${format}`); // File name
      document.body.appendChild(link);
      link.click();
      link.remove(); // Clean
      // up
      // the
      // link
      // element
    },
  });
};

export const useGetOfficialsAllAnimals = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["allAnimals"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get(
            "/animals/getAllRegisteredAnimals",
          );

          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached
        // data
        const cachedDAta = queryClient.getQueryData(["allAnimals"]);

        if (cachedDAta) {
          return cachedDAta;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(["allAnimals"]);
    },
    meta: {
      syncAction: async () => {
        const response = await apiClient.get("/admin/allAnimals");

        return response.data;
      },
    },
  });
};

export const useGetRandomFourAnimals = () => {
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["randomFourAnimals"],
    queryFn: async () => {
      if (onlineManager.isOnline() && !isRestoring) {
        try {
          const response = await apiClient.get("/animals/get-few-sila-Animals");
          return response.data;
        } catch (error) {
          throw error;
        }
      } else {
        // Optionally handle offline scenario or return cached data
        const cachedData = queryClient.getQueryData(["randomFourAnimals"]);
        if (cachedData) {
          return cachedData;
        } else {
          throw new Error("Offline and no cached data available");
        }
      }
    },
    networkMode: "offlineFirst",
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(["randomFourAnimals"]);
    },
    meta: {
      syncAction: async () => {
        const response = await apiClient.get("/animals/get-few-sila-Animals");
        return response.data;
      },
    },
  });
};

export const useExportAnimalData = (
  animalId: string,
  url: string, // e.g. "/admin/export-recipient-evaluation" or
  // "/admin/export-reproductive-process"
) => {
  return useMutation<void, Error, "csv" | "tsv" | "xlsx">({
    mutationFn: async (format) => {
      const response: AxiosResponse<Blob> = await apiClient.get(
        `${url}/${animalId}`,
        {
          responseType: "blob",
          withCredentials: true,
        }, // Ensure the
        // response
        // is treated
        // as a
        // binary
        // file
        // Adjust based
        // on your API
        // requirements
      );
      const urlParts = url.split("/");
      const fileType = urlParts[urlParts.length - 1] || "export";
      const downloadName = `${fileType}_${animalId}.${format}`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

export const useExportReproductiveRecipient = (
  animalId: string,
  exportUrl: string, // Now dynamic
) => {
  // Set the correct endpoint for reproductive recipient export
  return useMutation<void, Error, "csv" | "tsv" | "xlsx">({
    mutationFn: async (format) => {
      const response: AxiosResponse<Blob> = await apiClient.get(
        `${exportUrl}/${animalId}`,
        {
          responseType: "blob", // Ensure the response is treated as a binary file
          // withCredentials:
          // false,
          withCredentials: false,
        },
      );

      // Create a download
      // link for the file
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = blobUrl;
      link.setAttribute("download", `Animals-Sheet.${format}`); // File name
      document.body.appendChild(link);
      link.click();
      link.remove(); // Clean
      // up
      // the
      // link
      // element
    },
  });
};

export function exportToCSV(data: any[], filename: string) {
  if (!data.length) return;

  const keys = Object.keys(data[0]);
  const csvRows = [
    keys.join(","), // header row
    ...data.map((row) =>
      keys.map((k) => JSON.stringify(row[k] ?? "")).join(","),
    ),
  ];
  const csvContent = csvRows.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function exportToExcel(data: any[], filename: string) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, filename);
}

export const useSilaAdminAnimals = () => {
  return useQuery({
    queryKey: ["silaAdminAnimals"],
    queryFn: async () => {
      const res = await apiClient.get("/admin/sila-Animals/animals");
      return res.data;
    },
  });
};

export const useDeleteSilaAnimal = (animalId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["adminAnimalDelete"],
    mutationFn: async () => {
      try {
        const response = await apiClient.delete(
          `/admin/delete/sila-Animals/animal/${animalId}`,
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["silaAdminAnimals"] });
      const previousAnimals = queryClient.getQueryData<FormValues[]>([
        "silaAdminAnimals",
      ]);

      queryClient.setQueryData<FormValues[]>(
        ["silaAdminAnimals"],
        (old: any) => {
          if (!old) return [];
          return old.filter((animal: any) => animal._id !== animalId);
        },
      );

      return { previousAnimals };
    },
    onSuccess: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["adminAnimalRegister"] });
      toast.success("Animal has been Deleted  successfully");
    }, [queryClient]),
    onError: useCallback(
      (error: Error, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to Delete Animal");
        queryClient.setQueryData(
          ["adminAnimalRegister"],
          context?.previousAnimals,
        );
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["adminAnimalRegister"] });
    },
  });
};

export const useDeleteAnimal = () => {
  return useDeleteResource({
    resourceName: "Animal",
    queryKey: ["allAnimals"],
    getDeleteUrl: (id) => `/admin/delete/animals/animal/${id}`,
  });
};

export const useDeleteFarmer = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, string, { previousFarmers?: any[]; previousFarms?: any[] }>({
    mutationKey: ["delete", "Farmer"],
    mutationFn: async (farmerId: string) => {
      const response = await apiClient.delete(`/admin/delete/farmers/farmer/${farmerId}`);
      return response.data;
    },
    onMutate: async (farmerId: string) => {
      await queryClient.cancelQueries({ queryKey: ["allFarmers"] });
      await queryClient.cancelQueries({ queryKey: ["allFarms"] });

      const previousFarmers = queryClient.getQueryData<any[]>(["allFarmers"]);
      const previousFarms = queryClient.getQueryData<any[]>(["allFarms"]);

      queryClient.setQueryData<any[]>(["allFarmers"], (oldData) =>
        oldData ? oldData.filter((farmer) => farmer._id !== farmerId) : []
      );

      queryClient.setQueryData<any[]>(["allFarms"], (oldData) =>
        oldData ? oldData.filter((farm) => farm.farmOwnerId !== farmerId) : []
      );

      return { previousFarmers, previousFarms };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allFarmers"] });
      queryClient.invalidateQueries({ queryKey: ["allFarms"] });
      toast.success("Farmer and associated farm(s) removed successfully.");
    },
    onError: (error: Error, _farmerId: string, context) => {
      if (error instanceof AxiosError && error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to remove farmer and associated farms.");
      }
      if (context?.previousFarmers) {
        queryClient.setQueryData(["allFarmers"], context.previousFarmers);
      }
      if (context?.previousFarms) {
        queryClient.setQueryData(["allFarms"], context.previousFarms);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allFarmers"] });
      queryClient.invalidateQueries({ queryKey: ["allFarms"] });
    },
  });
};


interface Resource {
  _id: string;
}

interface UseDeleteResourceOptions {
  /** The user-facing name of the resource (e.g., "Farmer", "Animal") */
  resourceName: string;
  /** The React Query key for the list of these resources (e.g., ["allFarmers"]) */
  queryKey: QueryKey;
  /** A function that returns the delete API endpoint URL for a given ID */
  getDeleteUrl: (id: string) => string;
}

export const useDeleteResource = <T extends Resource>({
  resourceName,
  queryKey,
  getDeleteUrl,
}: UseDeleteResourceOptions) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string, { previousData?: T[] }>({
    mutationKey: ["delete", resourceName], // A unique key for this mutation
    mutationFn: async (resourceId: string) => {
      const url = getDeleteUrl(resourceId);
      const response = await apiClient.delete(url);
      return response.data;
    },

    onMutate: async (resourceId: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      queryClient.setQueryData<T[]>(
        queryKey,
        (oldData) => oldData?.filter((item) => item._id !== resourceId) ?? [],
      );

      return { previousData };
    },

    onSuccess: useCallback(() => {
      toast.success(`${resourceName} has been deleted successfully.`);
    }, [resourceName]),

    onError: useCallback(
      (error: Error, _variables: string, context: any) => {
        if (error instanceof AxiosError && error.response?.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(`Failed to delete ${resourceName}.`);
        }
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
      },
      [queryClient, queryKey, resourceName],
    ),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
